import React from "react";
import { useSnapshot } from "valtio";
import { AvForm, AvGroup } from "availity-reactstrap-validation";
import classnames from "classnames";
import {
    CheckboxIcon,
    CrossIcon,
    SettingsIcon,
    StyledIconButton,
    StyledLabel,
    StyledTextInput,
} from "@aureskonnect/react-ui";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { mutate } from "swr";
import { uuid } from "uuidv4";

import { store } from "@components/VerticalLayout/store";
import {
    setActions,
    setIsActionsClicked,
    setIsEdited,
    store as locationStore,
    setKey,
} from "./store";

import {
    CustomCardBody,
    CustomCardFooter,
    CustomCardHeader,
} from "@components/Common/CustomCardStyled";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { ipMatchWord } from "@constants/index";
import { ConfirmationEditMessage } from "@components/Common/ConfirmationEditMessage";
import { useFetchGlobal } from "../../../api/hooks";
import { RedisPublishFoodCourt } from "./helpers";
import { SupportCodeModal } from "@pages/Applications/DeviceAssociateModal/MoneticsApplicationForm/SupportCodeModal";
import { SoftaveraPayConfig } from "./SoftaveraPayConfig";

type LocationFormType = {
    setIsAddFoodCourtClicked: Function;
    isConsult: boolean;
    setIsConsult: Function;
    shopId: string;
    editedData: any;
};
export function FoodCourtForm({
    setIsAddFoodCourtClicked,
    isConsult,
    setIsConsult,
    shopId,
    editedData,
}: LocationFormType) {
    const { franchiseID, shopID } = useSnapshot(store);
    const { isActionsClicked, isEdited } = useSnapshot(locationStore);
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();

    const [shop, setShop] = React.useState<string>("");
    const [codeSupport, setCodeSupport] = React.useState<string>("");
    const [id, setId] = React.useState<string>("");
    const [ip, setIp] = React.useState<string>("");
    const [remark, setRemark] = React.useState<string>("");
    const [inputIp, setInputIp] = React.useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [isModalOpenedSupport, setIsModalOpenedSupport] = React.useState<
        boolean
    >(false);
    const [isValidCode, setIsValidCode] = React.useState<boolean>(false);
    const [isModalConfigOpened, setIsModalConfigOpened] = React.useState<
        boolean
    >(false);
    const [yavinId, setYavinId] = React.useState("");
    const [apiKey, setApiKey] = React.useState("");
    const [public_account_id, setPublicAccountId] = React.useState("");
    async function handleValidate() {
        toast.dismiss();
        if (!ip.match(ipMatchWord) && ip !== "") {
            setInputIp(true);
        } else if (
            editedData[t("Middleware IP address")] === ip &&
            editedData[t("Remark")] === remark &&
            editedData.softaveraPay_Yavin.public_account_id ===
                public_account_id &&
            editedData.softaveraPay_Yavin.yavinId === yavinId &&
            editedData.softaveraPay_Yavin.apiKey === apiKey
        ) {
            setIsModalOpened(true);
        } else {
            const apiUrlAdd = `${process.env.REACT_APP_API_V2_URL}/settings/general/foodCourt`;
            let data = {
                shopId: shopID,
                data: {
                    [id]: {
                        etat: ip === "" ? false : editedData.state,
                        horaires: editedData.hour,
                        remarque: remark,
                        middlewareIpAddress: ip,
                        softaveraPay_Yavin: {
                            yavinId: yavinId,
                            apiKey: apiKey,
                            public_account_id: public_account_id,
                        },
                    },
                },
            };

            const requestOptions = {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            };
            try {
                const response: any = await fetchGlobal(
                    apiUrlAdd,
                    requestOptions
                );
                if (response.error) {
                    throw Error("error");
                }
                mutate(apiUrlAdd, response);
                setKey(uuid());
                toast.success(
                    `${t("Middleware IP address configured successfully.")!}`,
                    {
                        position: toast.POSITION.TOP_CENTER,
                        theme: "colored",
                        icon: (
                            <CheckboxIcon height={25} width={25} fill="white" />
                        ),
                    }
                );
                setIp("");
                setRemark("");
                setPublicAccountId("");
                setApiKey("");
                setYavinId("");
                setIsAddFoodCourtClicked(false);
                setIsEdited(false);
                await RedisPublishFoodCourt(shopID, t, fetchGlobal);
            } catch (e) {
                toast.error(`${t("There's an error")!}`, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
        }
    }

    React.useEffect(() => {
        setShop(editedData[t("Shop")]);
        setId(editedData.Id);
        setIp(editedData[t("Middleware IP address")]);
        setRemark(editedData[t("Remark")]);
        setPublicAccountId(editedData.softaveraPay_Yavin.public_account_id);
        setApiKey(editedData.softaveraPay_Yavin.apiKey);
        setYavinId(editedData.softaveraPay_Yavin.yavinId);
        setInputIp(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedData, franchiseID, shopId, t]);

    return (
        <React.Fragment>
            <AvForm className="card">
                <CustomCardHeader
                    className="d-flex align-items-center justify-content-between"
                    style={{ maxHeight: "76px" }}
                >
                    <h5 className="pt-3">{t("Middleware IP address")}</h5>
                    <CrossIcon
                        className="icon-close-notification__clz mdi_icn_FRcloseForm"
                        height={13}
                        width={13}
                        onClick={() => {
                            setIsConsult(false);
                            if (isEdited === true) {
                                setIsActionsClicked(!isActionsClicked);
                                setActions(() => {
                                    setIsEdited(false);
                                    setIsAddFoodCourtClicked(false);
                                    setIsEdited(false);
                                });
                            } else {
                                setIsAddFoodCourtClicked!(false);
                                setIsEdited(false);
                            }
                        }}
                    />
                </CustomCardHeader>
                <CustomCardBody
                    className={`pl-3 pr-5 `}
                    style={{
                        maxHeight: "470px",
                        overflowY: " auto",
                    }}
                >
                    <AvGroup className={`${isConsult ? "pe-none__clz" : ""}`}>
                        <StyledLabel
                            htmlFor="path"
                            className="required__clz mt-3 "
                        >
                            {t("Shop")}
                        </StyledLabel>
                        <StyledTextInput
                            name="designation"
                            placeholder={t("Write")}
                            type="text"
                            autocomplete="off"
                            value={shop}
                            className="not-allowed-input__clz"
                        />
                    </AvGroup>
                    <AvGroup className={`${isConsult ? "pe-none__clz" : ""}`}>
                        <StyledLabel
                            htmlFor="path"
                            className="required__clz mt-3 "
                        >
                            {t("Shop identifier")}
                        </StyledLabel>
                        <StyledTextInput
                            name="designation"
                            placeholder={t("Write")}
                            type="text"
                            autocomplete="off"
                            value={id}
                            className="not-allowed-input__clz"
                        />
                    </AvGroup>
                    <AvGroup className={`${isConsult ? "pe-none__clz" : ""}`}>
                        <StyledLabel htmlFor="path" className=" mt-3 ">
                            {t("Middleware IP address")}
                        </StyledLabel>
                        <StyledTextInput
                            name="ip"
                            placeholder={t("Write")}
                            type="text"
                            autocomplete="off"
                            value={ip}
                            onChange={(e: any) => {
                                setIp(e.target.value);
                                setIsEdited(true);
                                if (
                                    !e.target.value.match(ipMatchWord) &&
                                    e.target.value !== ""
                                ) {
                                    setInputIp(true);
                                } else {
                                    setInputIp(false);
                                }
                            }}
                            className={classnames("", {
                                invalid__clz: inputIp,
                            })}
                        />
                        {inputIp ? (
                            <div
                                style={{
                                    width: "100%",
                                    marginTop: "0.25rem",
                                    fontSize: "80%",
                                    color: "#f46a6a",
                                }}
                            >
                                {t("IP address is invalid")!}
                            </div>
                        ) : null}
                    </AvGroup>

                    <AvGroup
                        className={`${
                            isConsult ? "pe-none__clz" : ""
                        } d-flex justify-content-between`}
                    >
                        <StyledLabel htmlFor="path" className=" mt-3 ">
                            {t("SoftaveraPay Configuration")}
                        </StyledLabel>
                        <StyledIconButton
                            icon="SettingsIcon"
                            onClick={() => {
                                setIsModalOpenedSupport(true);
                            }}
                        >
                            <SettingsIcon height={20} width={20} />
                        </StyledIconButton>
                    </AvGroup>
                    <AvGroup className={`${isConsult ? "pe-none__clz" : ""} `}>
                        <StyledLabel htmlFor="path" className=" mt-3 ">
                            {t("Remark")}
                        </StyledLabel>
                        <StyledTextInput
                            name="designation"
                            placeholder={t("Write")}
                            type="text"
                            autocomplete="off"
                            value={remark}
                            onChange={(e: any) => {
                                setRemark(e.target.value);
                                setIsEdited(true);
                            }}
                        />
                    </AvGroup>
                </CustomCardBody>
                <CustomCardFooter className="pt-1 pb-2">
                    <CustomSecondaryColorButton
                        outline
                        rounded
                        className="mr-2 mdi_btn_FRcancel cmn_btn_cancel"
                        variant="light"
                        onClick={() => {
                            if (isEdited === true) {
                                setIsActionsClicked(!isActionsClicked);
                                setActions(() => {
                                    setIsAddFoodCourtClicked(false);
                                    setIsEdited(false);
                                });
                            } else {
                                setIsAddFoodCourtClicked(false);
                                setIsEdited(false);
                            }
                            setIsConsult(false);
                        }}
                    >
                        <span> {t("Cancel")}</span>
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        className={`mdi_btn_FRvalidate cmn_btn_validate ${classnames(
                            { "not-allowed-input-vr__clz": isConsult }
                        )}`}
                        variant="primary"
                        onClick={handleValidate}
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </CustomCardFooter>
            </AvForm>
            <ConfirmationEditMessage
                isModalOpened={isModalOpened}
                setIsModalOpened={setIsModalOpened}
                setIsAddClicked={setIsAddFoodCourtClicked}
            />
            {isModalOpenedSupport ? (
                <SupportCodeModal
                    isModalOpened={isModalOpenedSupport}
                    setIsModalOpened={setIsModalOpenedSupport}
                    codeSupport={codeSupport}
                    setCodeSupport={setCodeSupport}
                    isValidCode={isValidCode}
                    setIsValidCode={setIsValidCode}
                    isConfig={true}
                    setIsModalConfigOpened={setIsModalConfigOpened}
                />
            ) : null}
            {isModalConfigOpened ? (
                <SoftaveraPayConfig
                    isModalOpened={isModalConfigOpened}
                    setIsModalOpened={setIsModalConfigOpened}
                    rowData={editedData}
                    publicAccountId={public_account_id}
                    setPublicAccountId={setPublicAccountId}
                    apiKey={apiKey}
                    setApiKey={setApiKey}
                    yavinId={yavinId}
                    setYavinId={setYavinId}
                    data={{ yavinId, apiKey, public_account_id }}
                />
            ) : null}
        </React.Fragment>
    );
}
